<script>
import { mapGetters } from 'vuex';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAccount } from 'dashboard/composables/useAccount';
import { useConversationListResize } from 'dashboard/composables/useConversationListResize';
import { useContactSidebarResize } from 'dashboard/composables/useContactSidebarResize';
import { useWindowSize } from '@vueuse/core';
import ChatList from '../../../components/ChatList.vue';
import ConversationBox from '../../../components/widgets/conversation/ConversationBox.vue';
import ConversationListResizeHandle from '../../../components/ConversationListResizeHandle.vue';
import wootConstants from 'dashboard/constants/globals';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import CmdBarConversationSnooze from 'dashboard/routes/dashboard/commands/CmdBarConversationSnooze.vue';
import { emitter } from 'shared/helpers/mitt';
import SidepanelSwitch from 'dashboard/components-next/Conversation/SidepanelSwitch.vue';
import ConversationSidebar from 'dashboard/components/widgets/conversation/ConversationSidebar.vue';

export default {
  components: {
    ChatList,
    ConversationBox,
    ConversationListResizeHandle,
    CmdBarConversationSnooze,
    SidepanelSwitch,
    ConversationSidebar,
  },
  beforeRouteLeave(to, from, next) {
    // Clear selected state if navigating away from a conversation to a route without a conversationId to prevent stale data issues
    // and resolves timing issues during navigation with conversation view and other screens
    if (this.conversationId) {
      this.$store.dispatch('clearSelectedState');
    }
    next(); // Continue with navigation
  },
  props: {
    inboxId: {
      type: [String, Number],
      default: 0,
    },
    conversationId: {
      type: [String, Number],
      default: 0,
    },
    label: {
      type: String,
      default: '',
    },
    teamId: {
      type: String,
      default: '',
    },
    conversationType: {
      type: String,
      default: '',
    },
    foldersId: {
      type: [String, Number],
      default: 0,
    },
  },
  setup() {
    const { uiSettings, updateUISettings } = useUISettings();
    const { accountId } = useAccount();
    const { listWidth, isDragging, startDrag, resetWidth } =
      useConversationListResize();
    const {
      sidebarWidth,
      isDragging: isDraggingSidebar,
      startDrag: startSidebarDrag,
      resetWidth: resetSidebarWidth,
    } = useContactSidebarResize();
    const { width: windowWidth } = useWindowSize();

    return {
      uiSettings,
      updateUISettings,
      accountId,
      listWidth,
      isDragging,
      startDrag,
      resetWidth,
      sidebarWidth,
      isDraggingSidebar,
      startSidebarDrag,
      resetSidebarWidth,
      windowWidth,
    };
  },
  data() {
    return {
      showSearchModal: false,
      // v6.4.70: Local flag to auto-hide contact sidebar on mobile/PWA
      // when entering a conversation. Resets when user manually opens it.
      mobileInitialHidden: false,
    };
  },
  computed: {
    ...mapGetters({
      chatList: 'getAllConversations',
      currentChat: 'getSelectedChat',
    }),
    showConversationList() {
      return this.isOnExpandedLayout ? !this.conversationId : true;
    },
    showMessageView() {
      return this.conversationId ? true : !this.isOnExpandedLayout;
    },
    isOnExpandedLayout() {
      const {
        LAYOUT_TYPES: { CONDENSED },
      } = wootConstants;
      const { conversation_display_type: conversationDisplayType = CONDENSED } =
        this.uiSettings;
      return conversationDisplayType !== CONDENSED;
    },

    // v6.4.70: Detect mobile/PWA screen size
    isSmallScreen() {
      return this.windowWidth < wootConstants.SMALL_SCREEN_BREAKPOINT;
    },

    shouldShowSidebar() {
      if (!this.currentChat.id) {
        return false;
      }

      // v6.4.70: On mobile/PWA, auto-hide sidebar when entering a conversation
      // to improve UX. The user can still manually open it via the toggle button.
      // Original code (before v6.4.70):
      // const { is_contact_sidebar_open: isContactSidebarOpen } = this.uiSettings;
      // return isContactSidebarOpen;
      if (this.isSmallScreen && this.mobileInitialHidden) {
        return false;
      }

      const { is_contact_sidebar_open: isContactSidebarOpen } = this.uiSettings;
      return isContactSidebarOpen;
    },
  },
  watch: {
    conversationId() {
      this.fetchConversationIfUnavailable();
      // v6.4.70: Reset mobile auto-hide flag when navigating to a new conversation
      if (this.isSmallScreen) {
        this.mobileInitialHidden = true;
      }
    },
    // v6.4.70: When user manually opens the sidebar on mobile (clicks toggle),
    // clear the mobileInitialHidden flag so it stays open
    'uiSettings.is_contact_sidebar_open'(newVal) {
      if (newVal && this.isSmallScreen) {
        this.mobileInitialHidden = false;
      }
    },
  },

  created() {
    // Clear selected state early if no conversation is selected
    // This prevents child components from accessing stale data
    // and resolves timing issues during navigation
    // with conversation view and other screens
    if (!this.conversationId) {
      this.$store.dispatch('clearSelectedState');
    }
    // v6.4.70: On mobile/PWA, auto-hide contact sidebar on initial load
    if (this.isSmallScreen) {
      this.mobileInitialHidden = true;
    }
  },

  mounted() {
    this.$store.dispatch('agents/get');
    this.$store.dispatch('portals/index');
    this.initialize();
    this.$watch('$store.state.route', () => this.initialize());
    this.$watch('chatList.length', () => {
      this.setActiveChat();
    });
  },

  methods: {
    onConversationLoad() {
      this.fetchConversationIfUnavailable();
    },
    initialize() {
      this.$store.dispatch('setActiveInbox', this.inboxId);
      this.setActiveChat();
    },
    toggleConversationLayout() {
      const { LAYOUT_TYPES } = wootConstants;
      const {
        conversation_display_type:
          conversationDisplayType = LAYOUT_TYPES.CONDENSED,
      } = this.uiSettings;
      const newViewType =
        conversationDisplayType === LAYOUT_TYPES.CONDENSED
          ? LAYOUT_TYPES.EXPANDED
          : LAYOUT_TYPES.CONDENSED;
      this.updateUISettings({
        conversation_display_type: newViewType,
        previously_used_conversation_display_type: newViewType,
      });
    },
    fetchConversationIfUnavailable() {
      if (!this.conversationId) {
        return;
      }
      const chat = this.findConversation();
      if (!chat) {
        this.$store.dispatch('getConversation', this.conversationId);
      }
    },
    findConversation() {
      const conversationId = parseInt(this.conversationId, 10);
      const [chat] = this.chatList.filter(c => c.id === conversationId);
      return chat;
    },
    setActiveChat() {
      if (this.conversationId) {
        const selectedConversation = this.findConversation();
        // If conversation doesn't exist or selected conversation is same as the active
        // conversation, don't set active conversation.
        if (
          !selectedConversation ||
          selectedConversation.id === this.currentChat.id
        ) {
          return;
        }
        const { messageId } = this.$route.query;
        this.$store
          .dispatch('setActiveChat', {
            data: selectedConversation,
            after: messageId,
          })
          .then(() => {
            emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, { messageId });
          });
      } else {
        this.$store.dispatch('clearSelectedState');
      }
    },
    onSearch() {
      this.showSearchModal = true;
    },
    closeSearch() {
      this.showSearchModal = false;
    },
  },
};
</script>

<template>
  <section class="flex w-full h-full min-w-0">
    <ChatList
      :show-conversation-list="showConversationList"
      :conversation-inbox="inboxId"
      :label="label"
      :team-id="teamId"
      :conversation-type="conversationType"
      :folders-id="foldersId"
      :is-on-expanded-layout="isOnExpandedLayout"
      :list-width="isOnExpandedLayout ? null : listWidth"
      @conversation-load="onConversationLoad"
    />
    <ConversationListResizeHandle
      v-if="!isOnExpandedLayout && showConversationList"
      :is-dragging="isDragging"
      @start-drag="startDrag"
      @reset-width="resetWidth"
    />
    <ConversationBox
      v-if="showMessageView"
      :inbox-id="inboxId"
      :is-on-expanded-layout="isOnExpandedLayout"
    >
      <SidepanelSwitch v-if="currentChat.id" />
    </ConversationBox>
    <ConversationSidebar
      v-if="shouldShowSidebar"
      :current-chat="currentChat"
      :sidebar-width="sidebarWidth"
      :is-dragging-sidebar="isDraggingSidebar"
      @start-sidebar-drag="startSidebarDrag"
      @reset-sidebar-width="resetSidebarWidth"
    />
    <CmdBarConversationSnooze />
  </section>
</template>
